'use client';

import { useEffect, useState } from 'react';
import Box from '@mui/system/Box';
import Modal from '@mui/material/Modal';
import CloseIcon from '@mui/icons-material/Close';
import { getCookie, setCookie, hasCookie } from 'cookies-next';
import { validEmail, X_GEO_LOCATION_STATUS, GEOLOCATION_ALLOWED, GEOLOCATION_NOT_ALLOWED } from 'utils';
import Typography from '@mui/material/Typography';
import MailOutlineOutlinedIcon from '@mui/icons-material/MailOutlineOutlined';
import Image from 'next/image';
import { BRAZE_RECIPE_MODAL } from 'braze';
import { getBrazeFlag } from 'harness-react';
import logger from 'logger';
import { RECIPE_MODAL_SHOWN } from '../constants/constants';
import EmailEntryVariant from './geolocationModal/textfieldEntryVariant';
import SuccessModal from './successModal';

export default function RecipeModal({
  content,
  postConsent,
  collectionPoint,
  consentPurposes,
  delay,
  createBrazeUser,
  trackBrazeUser,
}) {
  const geoLocationStatus = getCookie(X_GEO_LOCATION_STATUS) || GEOLOCATION_ALLOWED;
  const hasRecipeBeenVisitedBefore = hasCookie(RECIPE_MODAL_SHOWN);

  const [email, setEmail] = useState('');
  const [isEmailError, setIsEmailError] = useState(false);
  const [isError, setIsError] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [shouldShowModal, setShouldShowModal] = useState(false);

  const brazeEnabled = getBrazeFlag();

  useEffect(() => {
    const id = setTimeout(() => {
      setShouldShowModal(geoLocationStatus !== GEOLOCATION_NOT_ALLOWED && !hasRecipeBeenVisitedBefore);
      setCookie(RECIPE_MODAL_SHOWN, true, {
        secure: true,
        domain: 'hellolingo.com',
        maxAge: 3153600000,
      });
    }, delay);

    return () => {
      clearTimeout(id);
    };
  }, []);

  const handleClose = () => {
    setIsEmailError(false);
    setIsError(false);
    setShouldShowModal(false);
  };

  const onSignUp = async () => {
    if (!validEmail(email)) {
      setIsError(true);
      return;
    }

    if (email && brazeEnabled) {
      try {
        const externalId = await createBrazeUser(email, BRAZE_RECIPE_MODAL);
        await trackBrazeUser(externalId);
        await postConsent(collectionPoint, consentPurposes, externalId);

        setIsSuccess(true);
      } catch (error) {
        logger.error(error, 'Unable to complete recipe modal');
        setIsError(true);
      }
    }
  };

  return (
    <Modal
      open={shouldShowModal}
      aria-labelledby='parent-modal-title'
      aria-describedby='parent-modal-description'
      onClose={handleClose}
    >
      <Box
        data-testid='recipe-modal'
        width={{ xs: 1, md: 17 / 20, xl: 1032 }}
        sx={{
          position: 'absolute',
          overflow: 'auto',
          maxHeight: isSuccess ? 'inherit' : '100%',
          height: isSuccess ? 'inherit' : 'fit-content', // required for safari
          top: { md: '50%' },
          left: '50%',
          bottom: { xs: '0' },
          transform: { md: 'translate(-50%, -50%)', xs: 'translate(-50%)' },
          maxWidth: 1032,
          outline: 'none',
          display: 'flex',
          flexDirection: { md: 'row', xs: 'column' },
        }}
      >
        <CloseIcon
          sx={{
            cursor: 'pointer',
            fontSize: { md: '30px', xs: '18.75px' },
            position: 'absolute',
            top: { md: '24px', xs: '100px' },
            right: { md: '79px', xs: '27px' },
          }}
          onClick={() => handleClose()}
          data-testid='closeModalButton'
        />
        <Box
          display={{ xs: 'none', md: 'block' }}
          sx={{
            minWidth: { md: '336px', xs: '100%' },
            minHeight: { xs: '70px' },
            position: 'relative',
          }}
        >
          <Image
            height={710}
            width={336}
            style={{
              objectFit: 'cover',
              objectPosition: 'top center',
              width: '100%',
              height: '100%',
            }}
            alt={`${content.allowedUsersRecipeModal?.title}-Image`}
            src={content.allowedUsersRecipeModal?.desktopImage?._publishUrl}
            key='recipe_modal-desktop'
          />
        </Box>
        <Box
          display={{ xs: 'block', md: 'none' }}
          sx={{
            minWidth: { md: '336px', xs: '100%' },
            minHeight: { xs: '70px' },
            position: 'relative',
          }}
        >
          <Image
            fill
            style={{
              objectFit: 'cover',
              objectPosition: 'top center',
              width: '100%',
              height: '100%',
            }}
            alt={`${content.allowedUsersRecipeModal?.title}-Image`}
            src={content.allowedUsersRecipeModal?.mobileImage?._publishUrl}
            key='recipe_modal-mobile'
          />
        </Box>
        <Box
          sx={{
            color: 'colorTheme.contentPrimaryOnLight',
            display: 'flex',
            flexDirection: 'column',
            bgcolor: 'colorTheme.backgroundPrimaryLightWeb',
            boxShadow: 24,
            p: 4,
            outline: 0,
            padding: { md: '24px 80px 40px 80px', xs: '24px 27px 24px 27px' },
            justifyContent: isSuccess && 'center',
          }}
        >
          {!isSuccess && <MailOutlineOutlinedIcon sx={{ fontSize: '30px', mb: 2 }} />}

          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              flexDirection: 'column',
              bgcolor: 'colorTheme.backgroundPrimaryLightWeb',
            }}
          >
            {!isSuccess && (
              <>
                <EmailEntryVariant
                  title={content.allowedUsersRecipeModal?.title}
                  subtitle={content.allowedUsersRecipeModal?.subtitle}
                  boldSubtitle={content.allowedUsersRecipeModal?.boldSubtitle}
                  disclaimerText={content.allowedUsersRecipeModal?.disclaimerText?.plaintext}
                  buttonText={content.allowedUsersRecipeModal?.buttonText}
                  textfieldErrorMessage={content.allowedUsersRecipeModal?.textfieldErrorMessage}
                  setTextContent={setEmail}
                  inputLabel={content.allowedUsersRecipeModal?.textfieldLabel}
                  agreementLinks={content.allowedUsersRecipeModal?.agreementLinks}
                  onButtonCLick={onSignUp}
                  isError={isEmailError}
                  setIsError={setIsEmailError}
                  isSubmitError={isError}
                  styles={{
                    agreementLink: { mt: 2 },
                    title: { variant: 'h2' },
                    ctaBtn: { maxWidth: { xs: '100%', lg: '60%' } },
                  }}
                />
                <Typography
                  variant='bodySLink'
                  onClick={() => handleClose()}
                  sx={{
                    mt: 2,
                    cursor: 'pointer',
                    textAlign: { xs: 'center', md: 'left' },
                  }}
                >
                  {content.allowedUsersRecipeModal?.doNotShowMeThisAgain}
                </Typography>
              </>
            )}
            {isSuccess && (
              <SuccessModal
                handleClose={handleClose}
                title={content.successStateRecipeModal?.heading}
                subtitle={content.successStateRecipeModal?.subtext}
                buttonText={content.successStateRecipeModal?.buttonText}
              />
            )}
          </Box>
        </Box>
      </Box>
    </Modal>
  );
}
